// src/pages/FrameworkPage.jsx

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faOctagonCheck } from '@fortawesome/pro-regular-svg-icons';
import NavBarHome from '../Layouts/NavBarHome';
import FooterHome from '../Layouts/FooterHome';
import PageHeader from '../pagecomponents/PageHeader';

import skillsImg from '../pagecomponents/screenshots/framework/skills.png';
import profilesImg from '../pagecomponents/screenshots/framework/profiles.png';
import tapImg from '../pagecomponents/screenshots/framework/tap.png';
import blockersImg from '../pagecomponents/screenshots/framework/blockers.png';
import betherToolsImg from '../pagecomponents/screenshots/framework/betherTools.png';

const styles = {
  container: {
    fontFamily: "'Helvetica Neue', Arial, sans-serif",
    margin: 0,
    padding: 0,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
  },
  main: {
    flex: 1,
    padding: '40px 20px',
    backgroundColor: '#f9f9f9',
  },
  contentContainer: {
    width: '100%',
    backgroundColor: '#ff2300',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    boxSizing: 'border-box',
    minHeight: '700px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  tabContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
    marginBottom: '24px',
    flexWrap: 'wrap',
  },
  tabButton: {
    padding: '10px 20px',
    border: '2px solid #fff',
    borderRadius: '20px',
    backgroundColor: '#ff2300',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
  },
  activeTabButton: {
    padding: '10px 20px',
    border: 'none',
    borderRadius: '20px',
    backgroundColor: '#fff',
    color: '#ff2300',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
  },
  contentLayout: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
  },
  imageContainer: {
    flex: '1 1 60%',
    textAlign: 'center',
  },
  image: {
    width: '100%',
    maxWidth: '800px',
    height: 'auto',
    borderRadius: '8px',
  },
  textContainer: {
    flex: '1 1 35%',
    textAlign: 'left',
  },
  sectionText: {
    fontSize: '24px',
    color: '#fff',
    lineHeight: '1.6',
    marginBottom: '30px',
    fontWeight: '600',
  },
  bulletList: {
    listStyle: 'none',
    paddingLeft: '0',
    fontSize: '18px',
    color: '#fff',
    marginBottom: '24px',
    fontWeight: '400',
  },
  bulletItem: {
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  missionContainer: {
    maxWidth: '900px',
    margin: '0 auto',
    textAlign: 'center',
  },
  coreHeader: {
    textAlign: 'center',
    margin: '60px 0 40px',
  },
  coreTitle: {
    fontSize: '60px',
    margin: 0,
    fontWeight: '700',
    lineHeight: '1.1',
  },
  coreRed: {
    color: '#ff2300',
  },
  missionText: {
    fontSize: '18px',
    color: '#333',
    lineHeight: '1.7',
    marginBottom: '20px',
    fontWeight: '500',
    whiteSpace: 'pre-wrap',
  },
};

export default function FrameworkPage() {
  const [language, setLanguage] = useState(
    () => localStorage.getItem('language') || 'es'
  );

  // Detect mobile view
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleMediaChange = (e) => setIsMobile(e.matches);

    // Initial check
    handleMediaChange(mediaQuery);
    // Listen for screen size changes
    mediaQuery.addEventListener('change', handleMediaChange);

    return () => mediaQuery.removeEventListener('change', handleMediaChange);
  }, []);

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const content = {
    es: {
      pageHeader: {
        title: 'Framework Único de Desarrollo Competencial Digital',
        subtitle:
          'Convierte la gestión de competencias en un proceso completo, pero tan intuitivo como un clic. Olvida las plataformas complejas y los métodos subjetivos; despídete de la inconsistencia y la pérdida de tiempo.',
        ctaText: 'Solicitar Demo',
      },
      missionText:
        'Con nuestro Framework Único de Desarrollo Competencial Digital, dotamos a los líderes de herramientas inteligentes que convierten la evaluación en acción: miden el progreso sobre 24 habilidades clave, alinean expectativas con más de 100 perfiles de rol y ubican a cada colaborador en uno de los 9 niveles TAP® para activar planes de crecimiento personalizados; además, detectamos más de 91 bloqueadores de carrera y lanzamos rutas de desarrollo accionables para superarlos. De este modo, impulsas el crecimiento de tu equipo, refuerzas la confianza de los líderes y consigues que cada persona se sienta verdaderamente valorada.',
      sections: [
        {
          key: 'skills',
          title: 'Habilidades',
          text: 'Evalúa y desarrolla el talento de tu equipo con 24 habilidades clave',
          bullets: [
            'Evaluación automática para ahorrar tiempo',
            'Benchmarks del empleado que te da contexto',
            'Definición de competencias que unifica la comunicación entre empleados.',
          ],
        },
        {
          key: 'profiles',
          title: 'Perfiles',
          text: 'Alinea expectativas con más de 100 perfiles de rol diseñados para cada puesto',
          bullets: [
            'Perfiles predefinidos que unifican el proceso',
            'Comparación instantánea para saber que mejorar',
            'Análisis de brechas que marcan el camino hacia el siguiente nivel',
          ],
        },
        {
          key: 'tap',
          title: 'TAP®',
          text: 'Ubica a cada colaborador en uno de los 9 niveles TAP® y recibe un plan de acción personalizado',
          bullets: [
            'Identifica a tus colaboradores fundamentales',
            'Impulsa a los de alto potencial',
            'Acompaña a los de menor desempeño',
          ],
        },
        {
          key: 'blockers',
          title: 'Bloqueadores',
          text: 'Detecta más de 91 bloqueadores de carrera y elimina obstáculos con planes de desarrollo personalizados',
          bullets: [
            'Detección automática para no pasar nada por alto',
            'Planes específicos que abordan cada barrera',
            'Seguimiento visible asegura un cambio real y duradero.',
          ],
        },
        {
          key: 'betherTools',
          title: 'Herramientas Bether',
          text: 'Herramientas accionables para maximizar el impacto del dessarrollo de talento',
          bullets: [
            'Propuestas personalizadas para saltar a la acción',
            'Recordatorios inteligentes que generan hábitos constantes',
            'Herramients breves y prácticas para implementación rápida',
          ],
        },
      ],
    },
    en: {
      pageHeader: {
        title: 'Unique Digital Competency Development Framework.',
        subtitle:
          'Turn competency management into a complete process that’s as intuitive as a single click. Forget about complex platforms and subjective methods—say goodbye to inconsistency and wasted time.',
        ctaText: 'Request Demo',
      },
      missionText:
        'With our Unique Digital Competency Development Framework, we equip leaders with intelligent tools that turn assessment into action: they measure progress across 24 key skills, align expectations with over 100 role profiles, and place each team member in one of the 9 TAP® levels to trigger personalized growth plans; additionally, we detect over 91 career blockers and launch actionable development pathways to overcome them. In doing so, you drive your team’s growth, bolster leaders’ confidence, and ensure every individual feels genuinely valued.',
      sections: [
        {
          key: 'skills',
          title: 'Skills',
          text: 'Assess and develop your team’s talent with 24 key skills',
          bullets: [
            'Automatic assessment to save time',
            'Employee benchmarks that provide context',
            'Competency definitions that unify communication across employees',
          ],
        },
        {
          key: 'profiles',
          title: 'Profiles',
          text: 'Align expectations with over 100 role profiles tailored for each position',
          bullets: [
            'Predefined profiles that standardize the process',
            'Instant comparisons to identify improvement areas',
            'Gap analysis that charts the path to the next level',
          ],
        },
        {
          key: 'tap',
          title: 'TAP®',
          text: 'Place each team member into one of the 9 TAP® levels and receive a personalized action plan',
          bullets: [
            'Identify your core contributors',
            'Develop your high potentials',
            'Support your low performers',
          ],
        },
        {
          key: 'blockers',
          title: 'Blockers',
          text: 'Detect over 91 career blockers and remove obstacles with customized development plans',
          bullets: [
            'Automatic detection to ensure nothing is overlooked',
            'Targeted plans that address each barrier',
            'Visible tracking ensures real, lasting change',
          ],
        },
        {
          key: 'betherTools',
          title: 'Bether Tools',
          text: 'Actionable tools to maximize the impact of talent development',
          bullets: [
            'Personalized prompts to drive immediate action',
            'Smart reminders that build lasting habits',
            'Concise, practical guides for rapid implementation',
          ],
        },
      ],
    },
  };

  const lang = content[language];
  const [activeTab, setActiveTab] = useState(lang.sections[0].key);

  useEffect(() => {
    if (!lang.sections.find((s) => s.key === activeTab)) {
      setActiveTab(lang.sections[0].key);
    }
  }, [language, lang.sections, activeTab]);

  const current = lang.sections.find((s) => s.key === activeTab);
  const imageMap = {
    skills: skillsImg,
    profiles: profilesImg,
    tap: tapImg,
    blockers: blockersImg,
    betherTools: betherToolsImg,
  };
  const handleCtaClick = () => console.log('Demo solicitado');

  // Ajuste de tamaño de título en móvil
  const coreTitleStyle = {
    ...styles.coreTitle,
    fontSize: isMobile ? '32px' : styles.coreTitle.fontSize,
  };

  return (
    <div style={styles.container}>
      <NavBarHome language={language} />
      <PageHeader
        title={lang.pageHeader.title}
        subtitle={lang.pageHeader.subtitle}
        ctaText={lang.pageHeader.ctaText}
        onCtaClick={handleCtaClick}
      />

      {/* NUESTRO Framework Digital */}
      <main style={styles.main}>
        <div style={styles.missionContainer}>
          <hr
            style={{
              border: 'none',
              height: '2px',
              backgroundColor: '#ff2300',
              margin: '60px 0',
            }}
          />
          <div style={styles.coreHeader}>
            <h2 style={coreTitleStyle}>
              <span style={{ color: '#222', marginRight: '8px' }}>
                {language === 'es' ? 'Nuestro' : 'Our'}
              </span>
              <span style={styles.coreRed}>
                {language === 'es' ? 'Framework Digital' : 'Digital Framework'}
              </span>
            </h2>
          </div>
          <p style={styles.missionText}>{lang.missionText}</p>
          <hr
            style={{
              border: 'none',
              height: '2px',
              backgroundColor: '#ff2300',
              margin: '60px 0',
              marginBottom: '150px',
            }}
          />
        </div>

        {/* CONTENIDO PRINCIPAL */}
        <div style={styles.contentContainer}>
          <div style={styles.tabContainer}>
            {lang.sections.map((section) => (
              <button
                key={section.key}
                style={
                  section.key === activeTab
                    ? styles.activeTabButton
                    : styles.tabButton
                }
                onClick={() => setActiveTab(section.key)}
              >
                {section.title}
              </button>
            ))}
          </div>
          <div
            style={{
              ...styles.contentLayout,
              flexDirection: isMobile ? 'column' : 'row',
            }}
          >
            <div
              style={{
                ...styles.imageContainer,
                flex: isMobile ? '1 1 100%' : '1 1 60%',
                marginBottom: isMobile ? '20px' : 0,
              }}
            >
              <img
                src={imageMap[current.key]}
                alt={current.title}
                style={styles.image}
              />
            </div>
            <div
              style={{
                ...styles.textContainer,
                flex: isMobile ? '1 1 100%' : '1 1 35%',
                textAlign: 'left',
              }}
            >
              <p style={styles.sectionText}>{current.text}</p>
              <ul style={styles.bulletList}>
                {current.bullets.map((item, i) => (
                  <li key={i} style={styles.bulletItem}>
                    <FontAwesomeIcon icon={faOctagonCheck} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </main>

      <FooterHome language={language} setLanguage={setLanguage} />
    </div>
  );
}
